import { StateCreator } from 'zustand';
// FIX: 'MitarbeiterStatus' entfernt (Warnung gelöst), Rest ist Original
import { AppState, MonatsDaten, MitarbeiterLeistungData, FrozenStammdatenHJ1 } from '../types';
import { saveToLocalStorage } from '../utils';
import { isHj1WriteBlocked } from '../../utils/hjLock';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE DEFINITION
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

type MitarbeiterSliceType = Pick<AppState, 
    | 'initializeMitarbeiterLeistung' 
    | 'updateMitarbeiterLeistung' 
    | 'updateMonatsDatenFeld'
    | 'updateFrozenStammdatenHJ1'
>;

const buildInitialFrozenStammdatenHJ1 = (
    employee: any,
    leistungsprovision: any,
    extras?: { selectedDeviceIds?: string[]; lohnfaktorAktiv?: number; kostendeckungsfaktor1?: number }
): FrozenStammdatenHJ1 => {
    const provisionErstesHalbjahr = leistungsprovision?.erstesHalbjahr
        ? JSON.parse(JSON.stringify(leistungsprovision.erstesHalbjahr))
        : { provisionDienstleistung: 10, provisionVerkauf: 10 };

    return {
        name: employee?.name || '',
        wochenstunden: Number(employee?.wochenstunden) || 0,
        arbeitstage: Number(employee?.arbeitstage) || 5,
        grundgehalt: Number(employee?.grundgehalt) || 0,
        lohnzusatz: Number(employee?.lohnkostenzusatz ?? employee?.lohnzusatz) || 0,
        bonus: Number(employee?.bonus) || 0,
        taetigkeit: employee?.taetigkeit || '',
        zugehoerigkeit: employee?.zugehoerigkeit || '',
        country: employee?.country,
        kategorie: employee?.kategorie,
        grossWage: employee?.grossWage !== undefined ? Number(employee.grossWage) || 0 : undefined,
        netWage: employee?.netWage !== undefined ? Number(employee.netWage) || 0 : undefined,
        costoAziendaleOverride: employee?.costoAziendaleOverride !== undefined ? Number(employee.costoAziendaleOverride) || 0 : undefined,
        arbeitetMitGeraeten: Boolean(employee?.arbeitetMitGeraeten ?? employee?.hasGeraete),
        selectedDeviceIds: Array.isArray(extras?.selectedDeviceIds)
            ? [...extras!.selectedDeviceIds!]
            : undefined,
        lohnfaktorAktiv: extras?.lohnfaktorAktiv,
        kostendeckungsfaktor1: extras?.kostendeckungsfaktor1,
        provisionErstesHalbjahr,
    };
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE CREATOR
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const createMitarbeiterSlice: StateCreator<AppState, [], [], MitarbeiterSliceType> = (set, get) => ({

    initializeMitarbeiterLeistung: (employeeId) => set(state => { 
        if (state.clientData.mitarbeiterLeistung[employeeId]?.halbjahr1?.length) { return state; } 
        
        const initialMonatsDaten = (monat: string): MonatsDaten => ({ monat, bedienUmsatz: 0, vkUmsatz: 0, urlaubsTage: 0, krankheitsTage: 0, unproduktiveStunden: 0, unproduktiveStundenPlus: false, ueberstundenAuszahlen: false, festmachen: false }); 
        
        const newLeistung: MitarbeiterLeistungData = { 
            halbjahr1: ["Januar", "Februar", "März", "April", "Mai", "Juni"].map(initialMonatsDaten), 
            halbjahr2: ["Juli", "August", "September", "Oktober", "November", "Dezember"].map(initialMonatsDaten), 
            calculatedAvgVkFaktor: 0, 
            calculatedSumKrankTage: 0, 
            calculatedAvgEfficiency: 0, 
            calculatedStatusHJ1: 'neutral', 
            calculatedStatusHJ2: 'neutral' 
        }; 
        
        const newMitarbeiterLeistung = { ...state.clientData.mitarbeiterLeistung, [employeeId]: newLeistung }; 
        const newClientData = { ...state.clientData, mitarbeiterLeistung: newMitarbeiterLeistung }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData }; 
    }),

    updateMitarbeiterLeistung: (employeeId, data) => set(state => { 
        const newMitarbeiterLeistung = { 
            ...state.clientData.mitarbeiterLeistung, 
            [employeeId]: { ...(state.clientData.mitarbeiterLeistung[employeeId] || {}), ...data } 
        }; 
        const newClientData = { ...state.clientData, mitarbeiterLeistung: newMitarbeiterLeistung }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, isDirty: true }; 
    }),
    
    updateMonatsDatenFeld: (employeeId, halbjahr, monatIndex, field, value) => set(state => { 
        // Mandant: HJ1 gesperrt nach HJ2-Freigabe. God-Mode darf korrigieren.
        if (halbjahr === 'halbjahr1' && isHj1WriteBlocked(state.clientData)) {
            return state;
        }

        // 💉 CHIRURGIE-FIX: Korrekte Formatierung für das Undo-System!
        // Das Undo-System erwartet zwingend { clientData, settings }, nicht nur clientData!
        const historyEntry = {
            clientData: JSON.parse(JSON.stringify(state.clientData)),
            settings: JSON.parse(JSON.stringify(state.settings))
        };
        
        const newPast = [...state.past, historyEntry];
        if(newPast.length > 50) newPast.shift();

        const employeeLeistung = state.clientData.mitarbeiterLeistung[employeeId]; 
        if (!employeeLeistung || !employeeLeistung[halbjahr] || !employeeLeistung[halbjahr]![monatIndex]) { return state; } 
        
        const newLeistungData = JSON.parse(JSON.stringify(employeeLeistung)); 

        if (halbjahr === 'halbjahr1' && !state.clientData.isHJ1GesperrtGlobal && !newLeistungData.frozenStammdatenHJ1) {
            const employee = (state.clientData.personalData || []).find((p: any) => p?.id === employeeId);
            const leistungsprovision = (state.clientData as any)?.leistungsprovision || {};
            newLeistungData.frozenStammdatenHJ1 = buildInitialFrozenStammdatenHJ1(employee, leistungsprovision, {
                selectedDeviceIds: Array.isArray(newLeistungData.selectedDeviceIds) ? newLeistungData.selectedDeviceIds : [],
                kostendeckungsfaktor1: Number(state.clientData.lohnfaktorData?.kostendeckungsfaktor1) || 2.8,
            });
        }

        const targetMonat = newLeistungData[halbjahr][monatIndex]; 
        
        let sanitizedValue = value; 
        if (field === 'festmachen' || field === 'ueberstundenAuszahlen' || field === 'unproduktiveStundenPlus') { sanitizedValue = Boolean(value); } 
        else if (field !== 'monat') { sanitizedValue = Number(value) || 0; } 
        
        targetMonat[field] = sanitizedValue; 
        
        const newMitarbeiterLeistung = { ...state.clientData.mitarbeiterLeistung, [employeeId]: newLeistungData }; 
        const newClientData = { ...state.clientData, mitarbeiterLeistung: newMitarbeiterLeistung }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        
        // 💉 CHIRURGIE-FIX: isDirty: true hinzugefügt, damit das Cloud-Speichern erkannt wird
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    updateFrozenStammdatenHJ1: (employeeId, partial) => set(state => {
        const historyEntry = {
            clientData: JSON.parse(JSON.stringify(state.clientData)),
            settings: JSON.parse(JSON.stringify(state.settings))
        };
        const newPast = [...state.past, historyEntry];
        if (newPast.length > 50) newPast.shift();

        const employee = (state.clientData.personalData || []).find((p: any) => p?.id === employeeId);
        const leistungsprovision = (state.clientData as any)?.leistungsprovision || {};
        const employeeLeistung = state.clientData.mitarbeiterLeistung[employeeId] || {};
        const existingFrozen = employeeLeistung.frozenStammdatenHJ1 || buildInitialFrozenStammdatenHJ1(employee, leistungsprovision);

        const newFrozen: FrozenStammdatenHJ1 = {
            ...existingFrozen,
            ...partial,
            provisionErstesHalbjahr: partial.provisionErstesHalbjahr
                ? { ...existingFrozen.provisionErstesHalbjahr, ...partial.provisionErstesHalbjahr }
                : existingFrozen.provisionErstesHalbjahr,
        };

        const newLeistungData = { ...employeeLeistung, frozenStammdatenHJ1: newFrozen };
        const newMitarbeiterLeistung = { ...state.clientData.mitarbeiterLeistung, [employeeId]: newLeistungData };
        const newClientData = { ...state.clientData, mitarbeiterLeistung: newMitarbeiterLeistung };
        saveToLocalStorage({ ...state, clientData: newClientData });

        return { clientData: newClientData, past: newPast, future: [], isDirty: true };
    }),

});